/* Load only in view, retain the poster, and offer explicit playback if autoplay is blocked. */
if (!customElements.get('bestek-journey-background')) {
  customElements.define('bestek-journey-background', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const video = this.querySelector('video');
      if (!video) return;
      video.defaultMuted = true;
      video.muted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const narrow = matchMedia('(max-width: 1066px)');
      const events = new AbortController();
      const section = this.closest('.shopify-section') || this.parentElement;
      const button = section.querySelector('[data-bestek-journey-toggle="' + this.id + '"]');
      const status = section.querySelector('[data-bestek-journey-status="' + this.id + '"]');
      let inView = false;
      let disposed = false;
      let frame = 0;
      let abortRecoveryUsed = false;
      let failed = false;
      let blocked = false;
      let pending = false;
      let manual = false;
      let pausedByUser = false;
      let generation = 0;
      const allowed = () => !disposed && inView && !document.hidden && !pausedByUser && (!reduced.matches || manual);
      const updateButton = () => {
        if (disposed || !button) return;
        const playing = !video.paused && !video.ended && !failed;
        button.hidden = playing;
        button.textContent = failed ? 'Retry video' : playing ? 'Pause video' : 'Play video';
        button.setAttribute('aria-label', button.textContent);
      };
      const selectSource = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const key = narrow.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '') ? 'mobile' : 'desktop';
        video.poster = this.dataset[key + 'Poster'];
        const next = this.dataset[key + 'Src'];
        // Do not replace a loaded source during resize and incur a second video download.
        if (next && !video.getAttribute('src')) {
          this.removeAttribute('data-playing-frame');
          video.src = next;
          video.load();
        }
      };
      const sync = () => {
        if (reduced.matches && !manual) this.removeAttribute('data-playing-frame');
        if (!allowed() || failed) {
          if (!allowed()) abortRecoveryUsed = false;
          video.pause();
          updateButton();
          return;
        }
        selectSource();
        video.muted = true;
        if (video.paused && !pending && !blocked) {
          pending = true;
          const requestGeneration = ++generation;
          let retryAbortedPlayback = false;
          Promise.resolve(video.play()).then(() => {
            if (disposed) { if (!this.cleanup) video.pause(); return; }
            if (requestGeneration !== generation) return;
            if (!allowed()) video.pause();
          }).catch(error => {
            if (disposed || requestGeneration !== generation) return;
            if (error?.name === 'AbortError') {
              retryAbortedPlayback = allowed() && !abortRecoveryUsed;
              if (retryAbortedPlayback) abortRecoveryUsed = true;
              return;
            }
            blocked = true;
            this.removeAttribute('data-playing-frame');
            if (status) status.textContent = 'Video did not start automatically. Select Play video to watch.';
          }).finally(() => {
            if (disposed || requestGeneration !== generation) return;
            pending = false;
            updateButton();
            if (retryAbortedPlayback && allowed() && !blocked && !failed && !frame) {
              frame = requestAnimationFrame(() => { frame = 0; sync(); });
            }
          });
        }
        updateButton();
      };
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.02 }) : null;
      if (observer) observer.observe(this);
      else inView = true;
      video.addEventListener('canplay', sync, { signal: events.signal });
      video.addEventListener('playing', () => {
        if (!allowed()) { video.pause(); return; }
        this.setAttribute('data-playing-frame', '');
        blocked = false;
        abortRecoveryUsed = false;
        if (status) status.textContent = '';
        updateButton();
      }, { signal: events.signal });
      video.addEventListener('pause', () => { this.removeAttribute('data-playing-frame'); updateButton(); }, { signal: events.signal });
      video.addEventListener('error', () => {
        failed = true;
        pending = false;
        this.removeAttribute('data-playing-frame');
        if (status) status.textContent = 'Video could not load. Select Retry video to try again.';
        sync();
      }, { signal: events.signal });
      button?.addEventListener('click', () => {
        if (!video.paused && !failed) {
          pausedByUser = true;
          manual = false;
          video.pause();
          updateButton();
          return;
        }
        manual = true;
        pausedByUser = false;
        blocked = false;
        abortRecoveryUsed = false;
        const retry = failed;
        failed = false;
        pending = false;
        generation++;
        if (retry) video.load();
        sync();
      }, {signal: events.signal});
      document.addEventListener('visibilitychange', sync, { signal: events.signal });
      const resize = () => sync();
      const motionChanged = () => { manual = false; sync(); };
      narrow.addEventListener('change', resize);
      reduced.addEventListener('change', motionChanged);
      this.cleanup = () => {
        disposed = true;
        inView = false;
        generation++;
        pending = false;
        cancelAnimationFrame(frame);
        frame = 0;
        observer?.disconnect();
        events.abort();
        video.pause();
        narrow.removeEventListener('change', resize);
        reduced.removeEventListener('change', motionChanged);
        this.cleanup = null;
      };
      sync();
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
}
